/**
 * ElectronicModulesSection — the "Modules" panel on the Movement Manufacturer
 * detail page, parallel to CalibreFamiliesSection. Lists modules where this
 * manufacturer is the primary maker OR a co-developer, and opens the module
 * editor (pre-scoped to this manufacturer for new modules).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getElectronicModulesForManufacturer,
} from '../../services/firebase';
import type { ElectronicModule } from '../../types';
import { Button } from '../common/Button';
import { ElectronicModuleEditorModal } from './ElectronicModuleEditorModal';

export function ElectronicModulesSection({
  manufacturerId,
}: {
  manufacturerId: string;
}) {
  const [modules, setModules] = useState<ElectronicModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ElectronicModule | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setModules(await getElectronicModulesForManufacturer(manufacturerId));
    } catch (err) {
      console.error('Failed to load modules for manufacturer:', err);
    } finally {
      setLoading(false);
    }
  }, [manufacturerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (m: ElectronicModule) => {
    setEditing(m);
    setEditorOpen(true);
  };

  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">
          Modules{' '}
          <span className="text-sm font-normal text-gray-400">
            ({modules.length})
          </span>
        </h3>
        <Button variant="secondary" size="sm" onClick={openNew}>
          + Add module
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 italic">Loading modules…</div>
      ) : modules.length === 0 ? (
        <div className="text-sm text-gray-400 italic">
          No electronic modules for this manufacturer yet.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {modules.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => openEdit(m)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
              >
                <span className="font-medium text-gray-800 flex-1">
                  {m.name}
                  {m.manufacturerId !== manufacturerId && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">
                      co-developer
                    </span>
                  )}
                </span>
                {m.displayType && (
                  <span className="text-xs text-gray-400">{m.displayType}</span>
                )}
                <span className="text-xs text-gray-400">
                  {m.referenceCount ?? 0} refs
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <ElectronicModuleEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        module={editing}
        defaultManufacturerId={manufacturerId}
        onSaved={() => {
          // Keep the editor open (the modal self-manages closing) so a
          // just-created module can be AI-enriched in place; just refresh.
          void reload();
        }}
        onDeleted={() => {
          setEditorOpen(false);
          void reload();
        }}
      />
    </div>
  );
}
